"use client";

import { useMemo, useSyncExternalStore } from "react";
import { useLogin } from "@/src/core/auth/LoginContext";
import { getProfileSnapshot, subscribeProfile } from "@/src/core/auth/session";

const parseProfile = (raw: string | null) => {
  if (!raw) return undefined;
  try {
    const profile = JSON.parse(raw);
    return profile && Object.keys(profile).length > 0 ? profile : undefined;
  } catch {
    return undefined;
  }
};

/** Auth status for components; re-renders when the stored profile changes. */
export const useAuth = () => {
  const { openLoginModal } = useLogin();
  const raw = useSyncExternalStore(
    subscribeProfile,
    getProfileSnapshot,
    () => null,
  );
  const user_profile = useMemo(() => parseProfile(raw), [raw]);

  // Get other auth info from localStorage
  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("travela_auth_token") || undefined
      : undefined;
  const firebase_token =
    typeof window !== "undefined"
      ? localStorage.getItem("travela_firebase_token") || undefined
      : undefined;
  const user_phone =
    typeof window !== "undefined"
      ? localStorage.getItem("user_phone_number") || undefined
      : undefined;
  const id = (user_profile && user_profile.id) || undefined;

  const isAuthenticated = !!(user_profile && token);

  /**
   * Checks if user is authenticated, if not opens login modal
   * @param callback - Function to run if user is authenticated
   * @param onLoginModalOpen - Function to run when login modal opens (user not authenticated)
   * Returns true if authenticated, false if login modal was opened
   */
  const requireAuth = (
    callback?: () => void,
    onLoginModalOpen?: () => void,
  ) => {
    if (isAuthenticated) {
      if (callback) callback();
      return true;
    } else {
      if (onLoginModalOpen) onLoginModalOpen();
      openLoginModal();
      return false;
    }
  };

  /**
   * Opens login modal directly
   */
  const login = () => {
    openLoginModal();
  };

  return {
    isAuthenticated,
    user_profile,
    token,
    requireAuth,
    login,
    firebase_token,
    user_phone,
    id,
  };
};
